/**
 * Qual componente renderiza cada item da Configurações — K-2 da rodada CASCA.
 *
 * ── O que este arquivo resolve
 *
 * A rota `/admin/configuracoes/:secao/:item` precisa saber o que colocar no
 * painel direito. O `catalogo.ts` é a fonte do menu, da busca e das URLs, mas é
 * um arquivo de **dados** — não pode importar componente sem virar um segundo
 * lugar onde a tela é montada.
 *
 * Aqui é a ponte, e ela é um dicionário: `secao/item` → componente.
 *
 * ── Por que nem todo item está aqui
 *
 * Os itens cujo destino tem `?aba=` ou `#âncora` **não entram nesta leva**, e a
 * ausência é deliberada:
 *
 * - os cinco marcados `?aba=` moram todos na `ConfigCrmPage`, que tem 10 abas.
 *   Renderizá-la inteira no painel colocaria uma barra de abas dentro da casca —
 *   dois níveis de navegação, que é justamente o que a Configurações veio matar.
 *   Some quando a `ConfigCrmPage` aceitar receber a aba de fora (K-3, caso 1);
 * - os três marcados `#âncora` são pedaços da `AdminConfigPage`, e recortá-los em
 *   painéis próprios é o caso 2 do K-3.
 *
 * Um item sem entrada aqui continua funcionando pelo caminho antigo — o menu
 * leva para a rota de sempre. Não há tela quebrada; há tela ainda não migrada, e
 * o teste `itens-migrados.test.ts` mantém a conta honesta.
 */
import type { ComponentType } from 'react';

import { UsersPage } from '@/pages/admin/UsersPage';
import { ProfilesPage } from '@/pages/admin/ProfilesPage';
import { DepartmentsPage } from '@/pages/admin/DepartmentsPage';
import { PermissionsPage } from '@/pages/admin/PermissionsPage';
import { TiposAtividadePage } from '@/pages/admin/TiposAtividadePage';
import { ConfiguracoesClientesPage } from '@/pages/admin/ConfiguracoesClientesPage';
import { TagsCatalogPage } from '@/pages/admin/TagsCatalogPage';
import { CamposPersonalizadosListPage } from '@/pages/admin/custom-fields/CamposPersonalizadosListPage';
import { IntegracoesPage } from '@/pages/admin/IntegracoesPage';
import { PaginaOmie } from '@/pages/admin/integracoes/PaginaOmie';
import { PaginaAuvo } from '@/pages/admin/integracoes/PaginaAuvo';
import { PaginaWhatsApp } from '@/pages/admin/integracoes/PaginaWhatsApp';
import { PaginaEmail } from '@/pages/admin/integracoes/PaginaEmail';
import { PaginaGoogle } from '@/pages/admin/integracoes/PaginaGoogle';
import { PaginaVinculos } from '@/pages/admin/integracoes/PaginaVinculos';
import { ProdutosSumidosPage } from '@/pages/admin/integracoes/ProdutosSumidosPage';
import { OmieSyncStatusPage } from '@/pages/admin/OmieSyncStatusPage';
import { OmieAuditLogPage } from '@/pages/admin/OmieAuditLogPage';
import { ProdutosUniversalPage } from '@/pages/admin/ProdutosUniversalPage';
import { ProdutosConciliarPage } from '@/pages/admin/ProdutosConciliarPage';
import { FeatureFlagsPage } from '@/pages/admin/FeatureFlagsPage';
import { ErrosDeTelaPage } from '@/pages/admin/ErrosDeTelaPage';
// Catálogos, migração aba a aba (09/08/2026). Forma NORMAL, sem `embedded`: o
// `PageHeader` se cala sozinho dentro da casca.
import { ComprasConfigPage } from '@/pages/admin/compras/ComprasConfigPage';
import {
  CatalogosAvancados,
  CatalogoOrigens,
  CatalogoProdutos,
  CatalogoServicos,
} from './catalogos-wrappers';
import { MonitoramentoPadroes } from './MonitoramentoPadroes';
import { ConfiguracaoDaOs } from '@/pages/os-sistema/ConfiguracaoDaOs';
import { ConfiguracaoDoOrcamento } from '@/pages/manutencao/orcamento/ConfiguracaoDoOrcamento';
import { ProjetoDocumentoTiposPage } from '@/pages/admin/projetos/ProjetoDocumentoTiposPage';
import { ProjetoTiposPage } from '@/pages/admin/projetos/ProjetoTiposPage';
import { CooldownUnificacaoTab } from '@/pages/comercial/CooldownUnificacaoTab';

// K-3 · seção Comercial — as abas da ConfigCrmPage viram itens do menu.
import { AbaFases } from '@/pages/comercial/config-crm/AbaFases';
import { AbaListas } from '@/pages/comercial/config-crm/AbaListas';
import { AbaAcesso } from '@/pages/comercial/config-crm/AbaAcesso';
import { AbaAlertas } from '@/pages/comercial/config-crm/AbaAlertas';
import { LembreteConfigTab } from '@/pages/comercial/LembreteConfigTab';
import { ModelosOrcamentoTab } from '@/pages/comercial/ModelosOrcamentoTab';
import { ReciclagemConfigTab } from '@/pages/comercial/ReciclagemConfigTab';
import { OrcamentoPrecos, OrcamentoKits } from './orcamento-wrappers';
import { ConfiguracaoDaPesquisa } from '@/pages/os-sistema/ConfiguracaoDaPesquisa';
import {
  PainelFinanceiroContas,
  PainelFinanceiroMetas,
  PainelFinanceiroFinanciamento,
  PainelFinanceiroRelatorios,
  PainelSegurancaLogin,
} from './paineis-adminconfig';

/** Chave do registry: `<secao>/<item>`, os mesmos ids do `catalogo.ts`. */
export const ITEM_COMPONENTES: Record<string, ComponentType> = {
  // Pessoas e acesso
  'pessoas/permissoes': PermissionsPage,
  'pessoas/acesso-carteira': AbaAcesso,
  'pessoas/usuarios': UsersPage,
  'pessoas/perfis': ProfilesPage,
  'pessoas/departamentos': DepartmentsPage,

  // Catálogos
  'catalogos/listas-crm': AbaListas,
  'sistema/monitoramento-padroes': MonitoramentoPadroes,
  'sistema/ordens-de-servico': ConfiguracaoDaOs,
  'sistema/orcamento-da-manutencao': ConfiguracaoDoOrcamento,
  'sistema/pesquisa-de-satisfacao': ConfiguracaoDaPesquisa,
  'catalogos/tipos-atividade': TiposAtividadePage,
  'catalogos/catalogos-contrato': ConfiguracoesClientesPage,
  'catalogos/tags': TagsCatalogPage,
  'catalogos/campos-personalizados': CamposPersonalizadosListPage,
  'catalogos/compras': ComprasConfigPage,
  'catalogos/avancados': CatalogosAvancados,
  'catalogos/projeto-documentos': ProjetoDocumentoTiposPage,
  'catalogos/projeto-tipos': ProjetoTiposPage,
  'catalogos/produtos': CatalogoProdutos,
  'catalogos/servicos': CatalogoServicos,
  'catalogos/origens': CatalogoOrigens,

  // Comercial (K-3)
  'comercial/fases-funil': AbaFases,
  'comercial/lembretes': LembreteConfigTab,
  'comercial/unificar-propostas': CooldownUnificacaoTab,
  'comercial/orcamento-precos': OrcamentoPrecos,
  'comercial/orcamento-kits': OrcamentoKits,
  'comercial/modelos-orcamento': ModelosOrcamentoTab,
  'comercial/reciclagem-leads': ReciclagemConfigTab,

  // Financeiro (K-3, caso 2) — o item único virou quatro
  'financeiro/contas': PainelFinanceiroContas,
  'financeiro/metas-mes': PainelFinanceiroMetas,
  'financeiro/financiamento': PainelFinanceiroFinanciamento,
  'financeiro/relatorios': PainelFinanceiroRelatorios,

  // Sistema
  'sistema/seguranca-login': PainelSegurancaLogin,
  'sistema/alertas': AbaAlertas,
  'sistema/recursos-ativos': FeatureFlagsPage,
  // FUNDACOES A1 (17/08): o que quebrou pra alguém, sem depender de aviso.
  'sistema/erros-de-tela': ErrosDeTelaPage,

  // Integrações
  'integracoes/visao-geral': IntegracoesPage,
  // 06/09: uma página por integração — a visão geral virou só o estado.
  'integracoes/omie': PaginaOmie,
  'integracoes/auvo': PaginaAuvo,
  'integracoes/whatsapp': PaginaWhatsApp,
  'integracoes/email': PaginaEmail,
  'integracoes/google': PaginaGoogle,
  'integracoes/vinculos': PaginaVinculos,
  // A tela que a rodada URGENTE dos produtos fantasma deixou pendente.
  'integracoes/produtos-sumidos': ProdutosSumidosPage,
  'integracoes/omie-sincronizacao': OmieSyncStatusPage,
  'integracoes/omie-auditoria': OmieAuditLogPage,
  'integracoes/produtos-catalogo': ProdutosUniversalPage,
  'integracoes/produtos-conciliacao': ProdutosConciliarPage,
};

/** O componente deste item, ou `undefined` se ele ainda não migrou. */
export function componenteDoItem(secao: string, item: string): ComponentType | undefined {
  return ITEM_COMPONENTES[`${secao}/${item}`];
}
